class Cache {
  constructor () {
    /** @type {Map<String, String>} */
    this.cache = new Map()
  }

  exists (key) {
    return this.cache.has(key)
  }

  /**
   * @param {String} key
   * @param {String} value
   */
  set (key, value) {
    this.cache.set(key, value)
  }

  /**
   * @param {String} key
   */
  get (key) {
    return this.cache.get(key)
  }

  delete (key) {
    this.cache.delete(key)
  }
}

class DataFetcher {
  constructor () {
    this.cache = new Cache()
  }

  /**
   * @param {String} url
   * @param {String} section
   */
  async fetch (url, section) {
    return this.cache.exists(url)
      ? this.cache.get(url)
      : await this.#fetchData(url, section)
  }

  /**
   * @param {String} url
   * @param {String} section
   *
   * @returns {Promise<String>}
   */
  async #fetchData (url, section) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.cache.set(url, html)

    return html
  }
}

class BasePaginationRenderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  render (document) {
    this.renderProductGrid(document)
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderProductGrid (document) {
    this.element.throwError(`${this.name}.render not implemented`)
  }
}

class PaginationRenderer extends BasePaginationRenderer {
  renderProductGrid (document) {
    const productGrid = this.element.els['product-grid'].element
    if (!productGrid) return

    this.element.log('PaginationRenderer.renderProductGrid', {
      productGrid,
      document,
    })

    productGrid.innerHTML = window.BAO.Utils.getElement(
      this.element.els['product-grid'].selector,
      { context: document },
    ).innerHTML
  }
}

class Renderer {
  /**
   * @param {CollectionFilters} element
   */
  constructor (element) {
    this.element = element
    this.paginationRenderer = new PaginationRenderer(element)
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async render (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderFacets(newHTMLDom, event)
    this.renderActiveFacets(newHTMLDom)
    this.paginationRenderer.render(newHTMLDom)
    this.renderAdditional(newHTMLDom)

    this.element.loading = false
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} [event]
   *
   * @returns {void}
   */
  async renderQuickFilters (html, event) {
    const newHTMLDom = new DOMParser().parseFromString(html, 'text/html')

    this.renderQuickFiltersEl(newHTMLDom, event)

    this.element.loading = false
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderFacets (document, event) {
    const getFacetSelector = facet => {
      const selectorParts = {
        class: `.${Array.from(facet.classList).join('.')}`,
        additional: `${this.element.els.facet.selector}[data-index="${facet.dataset.index}"]`,
      }

      return Object.values(selectorParts).join('')
    }

    const currentFacetElements = this.element.els.facet.elements
    const facetThatSubmitted = event?.submitter?.closest(
      this.element.els.facet.selector,
    )

    const facetsToRenderSimply = event
      ? currentFacetElements.filter(facet => facet !== facetThatSubmitted)
      : currentFacetElements

    this.element.log('renderFacets', {
      currentFacetElements,
      facetThatSubmitted,
      facetsToRenderSimply,
    })

    facetsToRenderSimply.forEach(facet => {
      const selector = getFacetSelector(facet)

      const el = window.BAO.Utils.getElement(selector, { context: document })
      if (!el) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      facet.innerHTML = el.innerHTML
    })

    if (!facetThatSubmitted) return

    const facetThatSubmittedSelector = getFacetSelector(facetThatSubmitted)
    const facetElNamesToRender = ['content']

    facetElNamesToRender.forEach(elName => {
      const selector = `${facetThatSubmittedSelector} [data-filter-facet-el="${elName}"]`
      const currentEl = window.BAO.Utils.getElement(selector)
      const newEl = window.BAO.Utils.getElement(selector, { context: document })

      if (!currentEl) {
        this.element.throwError(
          `Could not find element with selector ${selector}`,
        )
      }

      currentEl.innerHTML = newEl.innerHTML
    })
  }

  /**
   * @param {Document} document
   * @param {SubmitEvent} event
   *
   * @returns {void}
   */
  renderQuickFiltersEl (document) {
    const quickFilters = this.element.externalEls['quick-filters'].element
    if (!quickFilters) return

    const newQuickFilters = window.BAO.Utils.getElement(
      this.element.externalEls['quick-filters'].selector,
      { context: document },
    )
    if (!newQuickFilters) return

    const newQuickFiltersEls = newQuickFilters.querySelectorAll(
      this.element.externalEls['quick-filter'].selector,
    )

    const quickFiltersEls = this.element.externalEls['quick-filter']?.elements
    if (!quickFiltersEls?.length) return

    const newInputsById = {}
    newQuickFiltersEls.forEach(el => {
      if (!el.id) return
      newInputsById[el.id] = el
    })

    quickFiltersEls.forEach(el => {
      const match = newInputsById[el.id]
      if (!match) return

      el.checked = match.checked
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderActiveFacets (document) {
    const activeFacets = this.element.els['active-facets'].elements
    if (activeFacets.length <= 0) return

    activeFacets.forEach(activeFacet => {
      const selectorParts = {
        class: `.${Array.from(activeFacet.classList).join('.')}`,
        id: `#${activeFacet.id}`,
        additional: `${this.element.els['active-facets'].selector}`,
      }
      if (activeFacets.length > 1 && !activeFacet.id) {
        throw new Error('Multiple active facets found, so all require an ID')
      }
      const selector = Object.values(selectorParts).join('')
      const activeFacetElement = document.querySelector(selector)

      this.element.log('renderActiveFacets', {
        parsedHTML: document,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      const currentEl = window.BAO.Utils.getElement(selector)
      if (!currentEl) return

      const hasActiveItems = !!window.BAO.Utils.getElement(
        `[${this.element._elIdentifier}="active-facet"]`,
        { context: activeFacetElement },
      )

      activeFacet.dataset.empty = (!hasActiveItems).toString()

      currentEl.innerHTML = activeFacetElement.innerHTML
    })
  }

  /**
   * @param {Document} document
   *
   * @returns {void}
   */
  renderAdditional (document) {
    const existingFiltersButtonText = window.BAO.Utils.getElement(
      '[data-mobile-filters-button-text]',
    )
    if (!existingFiltersButtonText) return

    const newFiltersButtonText = window.BAO.Utils.getElement(
      '[data-mobile-filters-button-text]',
      { context: document },
    )
    if (!newFiltersButtonText) return

    existingFiltersButtonText.innerHTML = newFiltersButtonText.innerHTML
  }
}

export class CollectionFilters extends window.BAO.CustomElement() {
  static get styles () {
    return `:host { display: block; }`
  }

  static get requiredElements () {
    return ['facets', 'facet', 'facet-summary', 'product-grid']
  }

  fetcher = new DataFetcher()
  renderer = new Renderer(this)

  constructor () {
    super()

    if (window.visualViewport.width <= 900) {
      document.body.classList.add('util-FixedBar-collection')
    }

    /**
     * sections - a list of sections that are related to this element. They can be updated by
     * calling the renderSections method with updated HTML
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: this.dataset.sectionId,
        section: this.dataset.sectionType,
        selector: '.shopify-section',
      },
      {
        id: this.externalEls['quick-filters']?.element?.dataset.sectionId,
        section: this.externalEls['quick-filters']?.element?.dataset
          .sectionType,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onLinkClick = this.onLinkClick.bind(this)

    this.listeners.add(this, 'submit', this.onFormSubmit.bind(this))
    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(this, 'click', this.onPaginationClick.bind(this))

    this.listeners.add(window, 'resize', this.onResize.bind(this))

    this.externalEls['quick-filter']?.elements.forEach(filterEl => {
      this.listeners.add(
        filterEl,
        'change',
        this.onExternalQuickFilterClick.bind(this),
      )
    })
  }

  /**
   * @param {MouseEvent} event
   */
  onExternalQuickFilterClick (event) {
    event.preventDefault()

    const input = event.target
    const form = input.closest('form')
    if (!form) return

    this.externalEls['quick-filter']?.elements.forEach(filter => {
      if (filter !== input) filter.checked = false
    })

    const filterLabelEl = event.currentTarget.parentElement
    filterLabelEl.classList.toggle('clc-Card_Active', input.checked)

    const currentParams = new URLSearchParams(window.location.search)

    const name = input.name
    const value = input.value

    const prevValues = currentParams.getAll(name)

    currentParams.delete(name)
    if (input.checked) {
      currentParams.append(name, value)
    } else {
      prevValues
        .filter(v => v !== value)
        .forEach(v => currentParams.append(name, v))
    }

    const searchParams = currentParams.toString()

    this.renderPage({ searchParams, event })

    window.drawers.activeDrawerKey = ''

    if (input.checked) {
      const offset = 20
      const elementTop = this.getBoundingClientRect().top
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop

      window.scrollTo({
        top: elementTop + scrollTop - offset,
        behavior: 'smooth',
      })
    }
  }

  /**
   * @param {MouseEvent} event
   */
  onLinkClick (event) {
    event.preventDefault()

    const link = event.target.closest('[data-facet-remove-el="link"]')
    if (!link) return
    const searchParams = new URLSearchParams(link.search)

    this.renderPage({ searchParams: searchParams.toString() })
  }

  onResize () {
    window.visualViewport.width <= 900
      ? document.body.classList.add('util-FixedBar-collection')
      : document.body.classList.remove('util-FixedBar-collection')
  }

  onFormSubmit (event) {
    if (event.target.classList.contains('prd-Form_Form')) {
      return
    }

    event.preventDefault()

    const form = event.target
    const currentParams = new URLSearchParams(window.location.search)

    for (const element of Array.from(form.elements)) {
      if (element.name) currentParams.delete(element.name)
    }

    for (const [key, value] of new FormData(form)) {
      currentParams.append(key, value)
    }

    const searchParams = currentParams.toString()

    this.renderPage({ searchParams, event })

    window.drawers.activeDrawerKey = ''

    if (window.BAO.MediaBreakpoints.isSm) return

    this.els['product-grid'].element.scrollIntoView({ behavior: 'smooth' })
  }

  /**
   * @param {PopStateEvent} event
   *
   * @returns {void}
   */
  onHistoryChange (event) {
    const searchParams = event.state.searchParams
    if (!searchParams) return

    if (searchParams.toString() === this.searchParams.previous.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  async onPaginationClick (event) {
    const pagination = event.target.closest(this.els?.pagination?.selector)
    if (!pagination) return

    const anchor = event.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) return

    event.preventDefault()

    const searchParams = new URLSearchParams(anchor.search)
    const page = searchParams.get('page') ?? '1'
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', page)

    await this.renderPage({ searchParams: currentSearchParams.toString() })

    this.scrollIntoView({ behavior: 'smooth' })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {SubmitEvent} [event]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  async renderPage ({ searchParams, event, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, event, updateURLHash })

    this.searchParams.previous = this.searchParams.current
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    for (const section of this.sections) {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      this.log('renderPageSections', { section, url })

      const html = await this.fetcher.fetch(url, section.id)

      if (section.id === this.dataset.sectionId) {
        await this.renderer.render(html, event)
      } else if (
        this.externalEls['quick-filters']?.element &&
        section.id ===
          this.externalEls['quick-filters'].element.dataset.sectionId
      ) {
        await this.renderer.renderQuickFilters(html)
      }
    }

    if (window.yotpo) {
      window.yotpo.refreshWidgets()
    }
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}
